import { Badge, Combobox, PillsInput, Pill, UnstyledButton, useCombobox, Stack } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useState } from 'react';

export interface AppointmentTypeOption {
  code: string;
  label: string;
  value: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
}

interface AppointmentTypeSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  appointmentTypes: AppointmentTypeOption[];
}

export function AppointmentTypeSelect({ value, onChange, appointmentTypes }: AppointmentTypeSelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  const [search, setSearch] = useState('');

  const handleValueSelect = (code: string) =>
    onChange(value.includes(code) ? value.filter((v) => v !== code) : [...value, code]);

  const handleValueRemove = (code: string) => onChange(value.filter((v) => v !== code));

  const options = appointmentTypes
    .filter((ap) => !value.includes(ap.code) && ap.label.toLowerCase().includes(search.trim().toLowerCase()))
    .map((ad) => (
      <Combobox.Option value={ad.code} key={ad.code} active={value.includes(ad.code)}>
        {ad.label}
      </Combobox.Option>
    ));

  return (
    <Stack gap="md">
      <Combobox store={combobox} onOptionSubmit={handleValueSelect} withinPortal={false}>
        <Combobox.DropdownTarget>
          <PillsInput onClick={() => combobox.openDropdown()} size="sm">
            <Pill.Group gap={4}>
              {value.map((code) => {
                const activityDef = appointmentTypes.find((ad) => ad.code === code)!;
                return (
                  <Badge
                    key={code}
                    variant="light"
                    size="lg"
                    style={{
                      color: activityDef.textColor,
                      backgroundColor: activityDef.backgroundColor,
                      border: `2px solid ${activityDef.borderColor}`,
                      paddingRight: 4,
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: 8,
                    }}
                    rightSection={
                      <UnstyledButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleValueRemove(code);
                        }}
                        style={{ display: 'flex', alignItems: 'center' }}
                        aria-label={`Remove ${activityDef.label}`}
                      >
                        <IconX size={12} style={{ color: activityDef.textColor }} />
                      </UnstyledButton>
                    }
                  >
                    {activityDef.label}
                  </Badge>
                );
              })}
              <Combobox.EventsTarget>
                <PillsInput.Field
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  value={search}
                  placeholder={value.length === 0 ? 'All appointment types' : undefined}
                  onChange={(event) => {
                    combobox.updateSelectedOptionIndex();
                    setSearch(event.currentTarget.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Backspace' && search.length === 0 && value.length > 0) {
                      event.preventDefault();
                      handleValueRemove(value[value.length - 1]);
                    }
                  }}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>
        {options.length > 0 ? (
          <Combobox.Dropdown>
            <Combobox.Options>{options}</Combobox.Options>
          </Combobox.Dropdown>
        ) : undefined}
      </Combobox>
    </Stack>
  );
}
